import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';

import {CommonDataService, Dependent, DependentBirthYearRange, EnumType, Format, PrimeNgConfig, PrimeNgSupportService, TableColumn, TableFormat, Validation} from '@app/shared';
import {classToClass} from "class-transformer";

/**
 * @author Archana Anand (date: June 2018)
 * @desc CRUD operations for Dependents of Employee
 * */

@Component({
  selector: 'app-dependent',
  templateUrl: './dependent.component.html',
  styleUrls: ['./dependent.component.css'],
  providers: [ConfirmationService]
})
export class DependentComponent implements OnInit, AfterViewInit {

  @Input() dependents: Dependent[];
  @Output() dependentChangeHandler = new EventEmitter<Dependent[]>();

  // standard format and validation
  DependentBirthYearRange = DependentBirthYearRange;

  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;

  readonly DefaultSortField = "type";

  tableColumns: TableColumn[];                                // column headers and fields to display on the table
  dependentTypeDropDown: SelectItem[];
  dependentToEdit: Dependent;                         // object used when add/edit selected row
  displayDialog: boolean;                             // when true, show the popup dialog to gather user input
  selectedRowIndex: number;                           // index of the selected row
  maxDobEndOn: Date;
  showEditSSN: boolean = false;

  @ViewChild('dependentAction') dependentActionTemplate: TemplateRef<any>;


  constructor(private commonDataService: CommonDataService, private confirmationService: ConfirmationService, private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.maxDobEndOn = new Date();
    this.getDependentTypes();
  }

  ngAfterViewInit(): void {
    this.buildMenu();
  }


  /**
   * @desc retrieve valid dependent types
   * @returns this.dependentTypeDropDown - retrieved valid dependent types as dropdown menu from the backend DB
   * */
  getDependentTypes(): void {
    this.commonDataService.getEnumPairAsDropdown(EnumType.Dependent).subscribe(dependentTypes => {
      this.dependentTypeDropDown = dependentTypes;
    });
  }

  /**
   * @desc show a pop-up dialog to gather new dependent information
   * @returns this.copiedRow - instantiated
   *          this.displayDialog - set to true, so popup dialog is shown to gather a new *dependent info
   */
  onClickAddDependent(): void {
    this.dependentToEdit = new Dependent();
    this.selectedRowIndex = -1;
    this.showEditSSN = true;
    this.displayDialog = true;
  }

  /**
   * @desc edit an existing dependent
   * @param dependent - selected dependent to edit
   * @param rowIndex -  index of selected  element in array
   * @returns this.copiedRow - has copy of the selected dependent
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditDependent(dependent: Dependent): void {
    const rowIndex = this.dependents.indexOf(dependent);
    this.dependentToEdit = classToClass(dependent);
    this.showEditSSN = false;
    this.selectedRowIndex = rowIndex;
    this.displayDialog = true;
  }

  triggerValueChangedEvent() {
    this.dependentChangeHandler.emit(this.dependents);
  }

  /**
   * @desc save new dependent to the inputArray
   * @param dependent - either new or selected dependent for edit
   * @returns: this.inputArray - new dependent added to the array
   */
  onClickSaveDependent(dependent: Dependent): void {
    if (this.selectedRowIndex >= 0) {
      this.dependents = this.primeNgService.replaceByIndex(this.dependents, dependent, this.selectedRowIndex);
      this.selectedRowIndex = -1;
    } else {
      this.dependents = this.primeNgService.unshift(this.dependents, dependent);
    }
    this.triggerValueChangedEvent();
    this.displayDialog = false;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param dependent - selected row to delete
   * @returns: this.inputArray - removed input row from the array
   */
  onClickDeleteDependent(dependent: Dependent): void {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this Dependent?',
      header: 'Delete Dependent',
      icon: 'fa fa-question-circle',
      accept: () => {
        this.dependents = this.primeNgService.deleteByObj(this.dependents, dependent);
        this.triggerValueChangedEvent();
      },
      reject: () => {
      }
    });
  }

  private buildMenu(): void {
    this.tableColumns = [
      {field: 'type', header: 'Type', format: TableFormat.TitleCase},
      {field: 'firstName', header: 'First Name'},
      {field: 'middleName', header: 'Middle Name'},
      {field: 'lastName', header: 'Last Name'},
      {field: 'ssn', header: 'SSN', format: TableFormat.SsnMask},
      {field: 'dateOfBirth', header: 'Date Of Birth', format: TableFormat.Date},
      {field: 'action', header: 'Action', template: this.dependentActionTemplate}
    ];
  }

}
